import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { fetchUserProfile } from '../store/userSlice';
import { getAipptToken, deductAipptBalance } from '../api/user';

const FALLBACK_TOKEN = (() => {
 const candidates = [
 process.env.REACT_APP_AIPPT_FALLBACK_TOKEN,
 process.env.REACT_APP_DOCMEE_FALLBACK_TOKEN,
 process.env.REACT_APP_DOCMEE_STATIC_TOKEN
 ];

 for (const candidate of candidates) {
 if (typeof candidate === 'string') {
 const normalized = candidate.trim();
 if (normalized) {
 return normalized;
 }
 }
 }

 return '';
})();

const STATUS_HINTS = {
 loading: '正在准备 Docmee UI 组件…',
 'fetching-token': '正在向服务器请求 Docmee token…',
 mounting: 'Docmee UI 组件挂载中…',
 ready: 'Docmee 组件已就绪，可开始创建演示文稿。',
 invalid: '当前 token 未通过认证，请刷新页面重新获取。',
 error: 'Docmee 组件加载失败，请稍后重试或联系管理员。',
 unauthenticated: '未检测到有效的用户信息，请登录后再试。'
};

const NOTIFICATION_TONE = {
 success: 'bg-green-50 border-green-200 text-green-700',
 warning: 'bg-accent-50 border-accent-200 text-accent-700',
 error: 'bg-red-50 border-red-200 text-red-700',
 info: 'bg-accent-50 border-accent-200 text-accent-700'
};

const DEFAULT_MESSAGES = {
 success: '操作成功',
 warning: '请注意该操作提示',
 error: '操作失败，请稍后重试。',
 info: '提示'
};

// 每次创作 PPT 的钻石消耗
const PPT_COST = 100;
// 每次生成完成后的扣费/更新消耗（由后端扣费接口返回成功后提示）
const PPT_UPDATE_COST = 99;

export default function AIPPTPage() {
 const dispatch = useDispatch();
 const containerRef = useRef(null);
 const docmeeInstanceRef = useRef(null);
 const userKeyRef = useRef(null);
 const notificationTimersRef = useRef({});
 const [status, setStatus] = useState('loading');
 const [errorDetail, setErrorDetail] = useState('');
 const [tokenOrigin, setTokenOrigin] = useState('pending');
 const [notifications, setNotifications] = useState([]);
 const [modalNotice, setModalNotice] = useState(null);

 const dismissNotification = (id) => {
 setNotifications((prev) => prev.filter((item) => item.id !== id));
 const timerId = notificationTimersRef.current[id];
 if (timerId) {
 window.clearTimeout(timerId);
 delete notificationTimersRef.current[id];
 }
 };

 const pushNotification = (type, content) => {
 const normalizedType = typeof type === 'string' && type ? type : 'info';
 const normalizedContent =
 typeof content === 'string'
 ? content
 : content == null
 ? ''
 : (() => {
 try {
 return JSON.stringify(content);
 } catch (error) {
 return String(content);
 }
 })();

 if (!normalizedContent) {
 return;
 }

 const id = `${Date.now()}-${Math.random().toString(16).slice(2)}`;
 setNotifications((prev) => [...prev, { id, type: normalizedType, content: normalizedContent }]);

 const timerId = window.setTimeout(() => {
 dismissNotification(id);
 }, 4000);
 notificationTimersRef.current[id] = timerId;
 };

 const notifyDocmee = (type, content) => {
 const instance = docmeeInstanceRef.current;
 const normalizedType = typeof type === 'string' ? type : undefined;
 const normalizedContent =
 typeof content === 'string'
 ? content
 : content == null
 ? ''
 : (() => {
 try {
 return JSON.stringify(content);
 } catch (error) {
 return String(content);
 }
 })();

 const toneKey = normalizedType || 'info';
 const message = normalizedContent || DEFAULT_MESSAGES[toneKey] || DEFAULT_MESSAGES.info;

 pushNotification(toneKey, message);
 setModalNotice({ type: toneKey, message });

 if (instance && typeof instance.sendMessage === 'function' && normalizedContent) {
 try {
 instance.sendMessage({ type: normalizedType, content: normalizedContent });
 } catch (error) {
 console.warn('[Docmee] sendMessage failed', error);
 }
 }
 };

 const isSuccessResponse = (code) => {
 if (code === undefined || code === null) {
 return false;
 }
 const numeric = Number(code);
 return Number.isFinite(numeric) && numeric === 0;
 };

 useEffect(() => {
 return () => {
 const timers = Object.values(notificationTimersRef.current);
 timers.forEach((id) => window.clearTimeout(id));
 notificationTimersRef.current = {};
 };
 }, []);

 useEffect(() => {
 const previousHtmlOverflow = document.documentElement.style.overflow;
 const previousBodyOverflow = document.body.style.overflow;
 const previousBodyBg = document.body.style.backgroundColor;

 document.documentElement.style.overflow = 'hidden';
 document.body.style.overflow = 'hidden';
 document.body.style.backgroundColor = '#f5f7ff';

 return () => {
 document.documentElement.style.overflow = previousHtmlOverflow;
 document.body.style.overflow = previousBodyOverflow;
 document.body.style.backgroundColor = previousBodyBg;
 };
 }, []);

 useEffect(() => {
 let disposed = false;

 const destroyDocmeeInstance = () => {
 const instance = docmeeInstanceRef.current;
 if (instance && typeof instance.destroy === 'function') {
 instance.destroy();
 }
 docmeeInstanceRef.current = null;
 };

 const safeSetStatus = (value) => {
 if (disposed) {
 return;
 }
 setStatus((prev) => (typeof value === 'function' ? value(prev) : value));
 };

 const safeSetErrorDetail = (value) => {
 if (disposed) {
 return;
 }
 setErrorDetail(value);
 };

 const safeSetTokenOrigin = (value) => {
 if (disposed) {
 return;
 }
 setTokenOrigin(value);
 };

 const resolveUserIdentity = () => {
 const candidateKeys = ['userInfo', 'userinfo'];
 const identity = { uid: null, userKey: null };

 for (const key of candidateKeys) {
 const raw = localStorage.getItem(key);
 if (raw) {
 try {
 const parsed = JSON.parse(raw);
 if (!identity.uid) {
 if (parsed?.uid) {
 identity.uid = parsed.uid;
 } else if (parsed?.email) {
 identity.uid = parsed.email;
 }
 }
 if (!identity.userKey) {
 if (parsed?.user_key) {
 identity.userKey = parsed.user_key;
 } else if (parsed?.userKey) {
 identity.userKey = parsed.userKey;
 }
 }
 } catch (error) {
 console.warn(`[Docmee] failed to parse ${key} from localStorage`, error);
 }
 }
 }

 if (!identity.uid) {
 const fallbackUid = localStorage.getItem('uid');
 if (fallbackUid) {
 identity.uid = fallbackUid;
 }
 }

 if (!identity.userKey) {
 const fallbackUserKey = localStorage.getItem('user_key');
 if (fallbackUserKey) {
 identity.userKey = fallbackUserKey;
 }
 }

 return identity;
 };

 const performBalanceRequestSync = (endpoint, userKey) => {
 try {
 const xhr = new XMLHttpRequest();
 xhr.open('POST', endpoint, false);
 xhr.setRequestHeader('Content-Type', 'application/json');

 const token = localStorage.getItem('token');
 if (token) {
 xhr.setRequestHeader('Authorization', `Bearer ${token}`);
 }

 xhr.send(JSON.stringify({ user_key: userKey }));

 const { status, responseText } = xhr;
 let data = null;

 try {
 data = responseText ? JSON.parse(responseText) : null;
 } catch (parseError) {
 console.warn('[Docmee] balance endpoint response parse failed', parseError);
 }

 return {
 ok: status >= 200 && status < 300,
 data,
 status,
 statusText: xhr.statusText
 };
 } catch (error) {
 console.error('[Docmee] balance endpoint request failed', error);
 return {
 ok: false,
 data: null,
 status: 0,
 statusText: error?.message || 'unknown error'
 };
 }
 };

 const bootstrapDocmee = async () => {
 safeSetStatus('loading');
 safeSetErrorDetail('');
 safeSetTokenOrigin('pending');

 const { uid, userKey } = resolveUserIdentity();
 userKeyRef.current = userKey || null;

 if (!uid || !userKey) {
 safeSetStatus('unauthenticated');
 safeSetErrorDetail('未检测到本地 uid 或 user_key，无法请求 Docmee token。');
 return;
 }

 let token = null;
 let selectedOrigin = 'api';
 let lastErrorMessage = '';

 try {
 safeSetStatus('fetching-token');
 const response = await getAipptToken({ uid });

 if (response?.code === 0 && response?.data?.token) {
 token = response.data.token;
 selectedOrigin = 'api';
 } else {
 lastErrorMessage = response?.message || `Token 服务响应异常（code=${response?.code ?? '未知'}）`;
 console.warn('[Docmee] token api returned non-success response', response);
 }
 } catch (apiError) {
 const reason =
 apiError?.response?.data?.message ||
 apiError?.message ||
 apiError?.code ||
 '请求 Docmee token 接口失败';
 lastErrorMessage = `无法连接 Docmee token 服务：${reason}`;
 console.error('[Docmee] token api request failed', apiError);
 }

 if (!token && FALLBACK_TOKEN) {
 console.warn('[Docmee] using fallback token from environment variable');
 token = FALLBACK_TOKEN;
 selectedOrigin = 'fallback-env';
 lastErrorMessage = '';
 }

 if (!token) {
 safeSetStatus('error');
 safeSetErrorDetail(
 lastErrorMessage || 'Docmee token 服务未返回有效 token，请检查后端代理或配置演示 token。'
 );
 return;
 }

 try {
 const { DocmeeUI } = await import('@docmee/sdk-ui');

 if (disposed || !containerRef.current) {
 return;
 }

 destroyDocmeeInstance();
 safeSetTokenOrigin(selectedOrigin);
 safeSetStatus('mounting');
 safeSetErrorDetail('');

 const preflightBalanceCheck = () => {
 const userKey = userKeyRef.current;
 if (!userKey) {
 notifyDocmee('error', '未找到用户额度信息，请重新登录后重试。');
 return false;
 }

 const result = performBalanceRequestSync(
 '/testapi/diamond/diamond_check_controller/diamond_balance_check_ppt',
 userKey
 );

 if (result.ok && isSuccessResponse(result.data?.code)) {
 // 余额充足，放行
 return true;
 }

 const baseMsg =
 result.data?.message ||
 result.statusText ||
 '当前可用额度不足，请充值或升级后再试。';
 const warningMessage = `${baseMsg}\n每创作一次 PPT 需要消耗 ${PPT_COST} 个钻石。`;
 notifyDocmee(result.ok ? 'warning' : 'error', warningMessage);
 return false;
 };

 const instance = new DocmeeUI({
 container: containerRef.current,
 page: 'creator',
 creatorVersion: 'v2',
 token,
 lang: 'zh',
 mode: 'light',
 padding: '24px 16px 16px',
 background: '#f5f7ff',
 isMobile: false,
 // 使用官方 onMessage 钩子拦截所有生成相关动作（包含“重新创作”）
 onMessage(message) {
 try {
 if (!message || !message.type) return;
 if (message.type === 'beforeGenerate') {
 return preflightBalanceCheck();
 }
 if (message.type === 'beforeCreateCustomTemplate') {
 return preflightBalanceCheck();
 }
 // 可按需扩展：下载前也可做额度校验
 // if (message.type === 'beforeDownload') {
 // return preflightBalanceCheck();
 // }
 } catch (e) {
 console.warn('[Docmee] onMessage handler error', e);
 }
 }
 });

 docmeeInstanceRef.current = instance;

 if (instance.on) {
 instance.on('mounted', (message) => {
 console.log('[Docmee] mounted', message);
 safeSetStatus('ready');
 safeSetErrorDetail('');
 });

 instance.on('user-info', (message) => {
 console.log('[Docmee] user info', message);
 safeSetStatus((current) => (current === 'invalid' || current === 'error' ? current : 'ready'));
 });

 const handleInvalidToken = (message) => {
 console.warn('[Docmee] invalid token', message);
 safeSetStatus('invalid');
 };

 // beforeGenerate 兼容处理（若 SDK 同时触发专用事件，则同样做额度校验）
 instance.on('beforeGenerate', () => preflightBalanceCheck());

 instance.on('charge', (message) => {
 console.log('[Docmee] charge', message);

 const userKey = userKeyRef.current;

 if (!userKey) {
 notifyDocmee('error', '未找到用户额度信息，扣费未执行。');
 return;
 }

 deductAipptBalance({ user_key: userKey })
 .then((response) => {
 if (isSuccessResponse(response?.code)) {
 // 扣费成功：弹窗展示详细提示，并刷新侧边栏用户信息
 setModalNotice({
 type: 'success',
 custom: 'charge-success',
 message: [
 '您的 PPT 正常生成中，关闭弹窗后，等待系统的自动生成。',
 `本次 AiPPT 耗费您 ${PPT_UPDATE_COST} 个钻石，已完成扣除。`,
 '若有错扣的情况，您可以随时联系客服。',
 '关闭此弹窗后可查看 AiPPT 生成的进度。'
 ].join('\n')
 });
 // 刷新用户信息，更新侧边栏左下角额度
 try {
 dispatch(fetchUserProfile());
 } catch (e) {
 console.warn('[AIPPT] refresh user profile after charge failed', e);
 }
 } else {
 const warning = response?.message || '扣费失败，请稍后重试或联系管理员。';
 notifyDocmee('warning', warning);
 }
 })
 .catch((deductError) => {
 const hint =
 deductError?.response?.data?.message ||
 deductError?.message ||
 '扣费失败，请稍后重试。';
 notifyDocmee('error', hint);
 });
 });

 instance.on('pageChange', (message) => {
 console.log('[Docmee] pageChange', message);
 });

 instance.on('invalid-token', handleInvalidToken);
 instance.on('error', (message) => {
 console.error('[Docmee] error', message);
 const code = message?.code ?? message?.data?.code;
 if (code === 88) {
 const base = message?.message || message?.data?.message || '您的次数已用完，请充值后再试。';
 notifyDocmee('warning', `${base}\n每创作一次 PPT 需要消耗 ${PPT_COST} 个钻石。`);
 }
 safeSetStatus('error');
 safeSetErrorDetail(message?.message || message?.data?.message || 'Docmee 组件内部错误');
 });

 instance.on('message', (message) => {
 if (message?.type === 'invalid-token') {
 handleInvalidToken(message);
 }
 });
 } else {
 safeSetStatus('ready');
 }
 } catch (error) {
 console.error('[Docmee] init failed', error);
 safeSetStatus('error');
 safeSetErrorDetail(error?.message || 'Docmee 组件初始化失败');
 }
 };

 bootstrapDocmee();

 return () => {
 disposed = true;
 destroyDocmeeInstance();
 };
 }, []);

 const baseStatusMessage = STATUS_HINTS[status] ?? '';
 const tokenOriginHints = {
 api: '',
 pending: '',
 'fallback-env': '（使用环境演示 token）'
 };
 const shouldAppendOrigin = !['error', 'invalid', 'unauthenticated'].includes(status);
 const originSuffix = shouldAppendOrigin ? tokenOriginHints[tokenOrigin] || '' : '';
 const statusMessage = [baseStatusMessage, originSuffix].filter(Boolean).join('');
 const showOverlay = status !== 'ready';
 const isLoadingPhase = ['loading', 'fetching-token', 'mounting'].includes(status);

 return (
 <div className="ppt-workspace h-screen w-full bg-cream-100 relative overflow-hidden">
 {isLoadingPhase && (
 <div className="absolute top-0 left-0 right-0 h-1.5 bg-ink-100/70 z-40 overflow-hidden">
 <div className="absolute inset-y-0 left-[-40%] w-2/5 bg-ink-900/80 animate-loading-sweep" />
 </div>
 )}
 {modalNotice && (
 <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink-900/40 backdrop-blur-sm">
 <div className="bg-cream-50 rounded-xl shadow-md max-w-md w-[92%] p-6 border border-ink-200">
 <div className="flex items-center justify-between mb-4">
 <h3 className="text-lg font-semibold text-ink-900">
 {modalNotice.type === 'success'
 ? '操作成功'
 : modalNotice.type === 'warning'
 ? '温馨提示'
 : modalNotice.type === 'error'
 ? '操作失败'
 : '提示'}
 </h3>
 <button
 type="button"
 onClick={() => setModalNotice(null)}
 className="h-8 w-8 rounded-full bg-cream-100 text-ink-500 hover:text-ink-900 hover:bg-ink-100 transition-colors"
 aria-label="关闭"
 >
 ×
 </button>
 </div>

 {modalNotice.custom === 'charge-success' ? (
 <div className="space-y-4">
 <div className="flex items-center gap-3 text-green-700 bg-green-50 border border-green-100 rounded-2xl px-3 py-2">
 <span className="inline-block h-2.5 w-2.5 rounded-full bg-green-500" aria-hidden="true" />
 <span className="text-sm font-medium">已完成扣费并开始生成</span>
 </div>
 <ul className="space-y-2 text-sm text-ink-700">
 <li className="flex items-start gap-2">
 <span className="mt-1 h-1.5 w-1.5 rounded-full bg-ink-400" aria-hidden="true" />
 <span>您的 PPT 正常生成中，关闭弹窗后，等待系统的自动生成。</span>
 </li>
 <li className="flex items-start gap-2">
 <span className="mt-1 h-1.5 w-1.5 rounded-full bg-ink-400" aria-hidden="true" />
 <span>本次 AiPPT 耗费您 99 个钻石，已经扣除。</span>
 </li>
 <li className="flex items-start gap-2">
 <span className="mt-1 h-1.5 w-1.5 rounded-full bg-ink-400" aria-hidden="true" />
 <span>若有错扣的情况，您可以随时联系客服。</span>
 </li>
 <li className="flex items-start gap-2">
 <span className="mt-1 h-1.5 w-1.5 rounded-full bg-ink-400" aria-hidden="true" />
 <span>关闭此弹窗 查看 AiPPT 生成的进度。</span>
 </li>
 </ul>
 </div>
 ) : (
 <p className="text-sm text-ink-600 leading-relaxed whitespace-pre-line">
 {modalNotice.message}
 </p>
 )}

 <div className="mt-6 flex justify-end">
 <button
 type="button"
 onClick={() => setModalNotice(null)}
 className="px-4 py-2 rounded-xl bg-ink-900 text-white text-sm font-medium shadow hover:bg-ink-800 transition-colors"
 >
 {modalNotice.custom === 'charge-success' ? '关闭并查看进度' : '知道了'}
 </button>
 </div>
 </div>
 </div>
 )}
 <div className="fixed top-6 right-6 z-50 flex flex-col gap-3 max-w-sm">
 {notifications.map((item) => {
 const tone = NOTIFICATION_TONE[item.type] || NOTIFICATION_TONE.info;
 return (
 <div
 key={item.id}
 className={`rounded-2xl border px-4 py-3 shadow-lg backdrop-blur-sm bg-opacity-90 flex items-start gap-3 ${tone}`}
 >
 <div className="flex-1 text-sm leading-relaxed">{item.content}</div>
 <button
 type="button"
 onClick={() => dismissNotification(item.id)}
 className="text-xs font-semibold opacity-70 hover:opacity-100 transition-opacity"
 aria-label="关闭提示"
 >
 ×
 </button>
 </div>
 );
 })}
 </div>
 <div ref={containerRef} className="absolute inset-0 w-full h-full" />
 <div
 className={`absolute inset-0 flex items-center justify-center transition-opacity duration-300 ${
 showOverlay ? 'opacity-100' : 'opacity-0 pointer-events-none'
 }`}
 >
 <div className="px-6 max-w-xl text-center space-y-4">
 {isLoadingPhase && (
 <div className="flex items-center justify-center gap-3">
 <div className="h-6 w-6 rounded-full border-2 border-ink-300 border-t-ink-900 animate-spin" aria-hidden="true" />
 <span className="text-sm font-medium text-ink-700">加载中</span>
 </div>
 )}
 <p className="text-sm text-ink-500">{statusMessage || 'Docmee UI 正在初始化...'}</p>
 {errorDetail && (
 <p className="text-xs text-red-500/80 bg-red-50/70 border border-red-100 px-3 py-2 rounded-xl">
 {errorDetail}
 </p>
 )}
 {tokenOrigin === 'fallback-env' && !['error', 'invalid'].includes(status) && (
 <p className="text-[11px] text-ink-500 leading-relaxed">
 当前使用环境变量中的演示 token，仅供联调体验，务必在生产环境接入服务端 token 创建流程。
 </p>
 )}
 {status === 'error' && (
 <p className="text-[11px] text-ink-500 leading-relaxed">
 请确认服务端 `get_aippt_token` 接口是否可用，或联系管理员更新 Docmee token 策略。
 </p>
 )}
 </div>
 </div>
 </div>
 );
}
